import { Controller } from "@hotwired/stimulus";
import Dropzone from "dropzone";
import { DirectUpload } from "@rails/activestorage";

export default class extends Controller {
  static targets = ["form", "dropzoneArea", "input", "existingImage", "hiddenInput", "message", "submitButton"];
  static values = {
    acceptedFileTypes: String,
    hiddenInputName: String,
    maxFiles: {type: Number, default: null},
    maxFileSize: {type: Number, default: null}
  };

  connect() {
    this.hideFileInput();
    this.initializeDropzone();
    this.preloadExistingImages();
    this.bindEvents();
  }

  hideFileInput() {
    this.inputTarget.disabled = true;
    this.inputTarget.style.display = "none";
  }

  get headers() {
    return { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute('content') };
  }

  initializeDropzone() {
    this.dropzone = new Dropzone(this.dropzoneAreaTarget, {
      url: "/rails/active_storage/direct_uploads",
      paramName: "file",
      maxFilesize: this.maxFileSizeValue,
      acceptedFiles: this.acceptedFileTypesValue,
      addRemoveLinks: true,
      dictDefaultMessage: "",
      autoProcessQueue: false,
      parallelUploads: 5,
      headers: this.headers,
      maxFiles: this.maxFilesValue,
    });
  }

  openFileDialog() {
    this.dropzoneAreaTarget.click();
  }

  preloadExistingImages() {
    this.existingImageTargets.forEach((image, index) => {
      const mockFile = { name: image.alt};
      this.dropzone.emit("addedfile", mockFile);
      this.dropzone.emit("thumbnail", mockFile, image.src);
      this.dropzone.emit("complete", mockFile);
      this.dropzone.files.push(mockFile);
      mockFile.hiddenInputElement = this.hiddenInputTargets[index];
    });
  }

  bindEvents() {
    this.dropzone.on("addedfile", file => {
      setTimeout(() => {
        if (file.accepted) this.createDirectUpload(file);
      }, 500);
    });

    this.dropzone.on("removedfile", file => {
      this.showDropzoneMessage();
      this.toggleSubmitButton();
      this.removeHiddenInput(file)
    });

    this.dropzone.on("canceled", file => {
      if (file.xhr) file.xhr.abort();
    });

    this.dropzone.on("maxfilesexceeded", file => {
      if (file.xhr) file.xhr.abort();
    });

    this.dropzone.on("complete", file => {
      this.toggleSubmitButton();
    });
  }

  createDirectUpload(file) {
    const upload = new DirectUpload(file, this.dropzone.options.url, {
      directUploadWillStoreFileWithXHR: xhr => {
        // Bind progress event here
        this.bindProgressEvent(file, xhr);
      }
    });

    file.status = Dropzone.UPLOADING;

    upload.create((error, blob) => {
      if (error) {
        this.dropzone.emit("error", file, error);
        file.status = Dropzone.ERROR;
      } else {
        this.addHiddenInput(blob.signed_id, file);
        this.hideDropzoneMessage();
        this.dropzone.emit("success", file);
        this.dropzone.emit("complete", file);
        file.status = Dropzone.SUCCESS;
      }
    });
  }

  bindProgressEvent(file, xhr) {
    file.xhr = xhr;
    xhr.upload.addEventListener("progress", event => {
      const progress = (event.loaded / event.total) * 100;
      const progressBar = file.previewElement.querySelector(".dz-upload");
      if (progressBar) progressBar.style.width = `${progress}%`;
    });
  }

  addHiddenInput(signedId, file) {
    const hiddenInput = document.createElement("input");
    hiddenInput.type = "hidden";
    hiddenInput.name = this.hiddenInputNameValue;
    hiddenInput.value = signedId;
    file.hiddenInputElement = hiddenInput;
    this.formTarget.appendChild(hiddenInput);
  }

  removeHiddenInput(file) {
    if (file.hiddenInputElement) file.hiddenInputElement.remove();
  }

  hideDropzoneMessage() {
    if (!this.hasMessageTarget) return;
    this.messageTarget.classList.add("hidden");
  }

  showDropzoneMessage() {
    if (!this.hasMessageTarget) return;
    if (this.dropzone.files.length > 0) return;
    this.messageTarget.classList.remove("hidden");
  }

  toggleSubmitButton() {
    if (!this.hasSubmitButtonTarget) return;
    this.submitButtonTarget.disabled = this.dropzone.files.length === 0;
  }
}